import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../Context/AuthContext'
import Loader from '../../Common/Loader'
import logo from '../assets/logo.svg'
import './ResetPassword.css'

function ResetPassword() {
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [message, setMessage] = useState('')
  const [messageType, setMessageType] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [displayedText, setDisplayedText] = useState('')
  const [showCursor, setShowCursor] = useState(true)
  
  const navigate = useNavigate()
  const { updatePassword, user, session, isPasswordRecovery } = useAuth()
  
  // Subtitle text for typing animation
  const subtitleText = "Your One-Stop Business Solution"

  // Typing animation effect
  useEffect(() => {
    let currentIndex = 0
    const typingInterval = setInterval(() => {
      if (currentIndex <= subtitleText.length) {
        setDisplayedText(subtitleText.slice(0, currentIndex))
        currentIndex++
      } else {
        clearInterval(typingInterval)
        setShowCursor(true)
      }
    }, 50)

    return () => clearInterval(typingInterval)
  }, [])

  useEffect(() => {
    // Check if user has a valid password recovery session
    if (!session || !user) {
      setMessage('Invalid or expired reset link. Please request a new password reset.')
      setMessageType('error')
      setTimeout(() => navigate('/forgot-password'), 3000)
      return
    }

    // If not a password recovery session, redirect to login
    if (!isPasswordRecovery) {
      navigate('/login')
      return
    }
  }, [session, user, isPasswordRecovery, navigate])

  const validatePassword = (password) => {
    if (password.length < 8) {
      return 'Password must be at least 8 characters long.'
    }
    if (!/(?=.*[a-z])/.test(password)) {
      return 'Password must contain at least one lowercase letter.'
    }
    if (!/(?=.*[A-Z])/.test(password)) {
      return 'Password must contain at least one uppercase letter.'
    }
    if (!/(?=.*\d)/.test(password)) {
      return 'Password must contain at least one number.'
    }
    return null
  }

  const handlePasswordUpdate = async () => {
    if (!newPassword || !confirmPassword) {
      setMessage('Please fill in both password fields.')
      setMessageType('error')
      return
    }

    if (newPassword !== confirmPassword) {
      setMessage('Passwords do not match.')
      setMessageType('error')
      return
    }

    const passwordError = validatePassword(newPassword)
    if (passwordError) {
      setMessage(passwordError)
      setMessageType('error')
      return
    }

    setIsLoading(true)
    setMessage('')

    try {
      const { error } = await updatePassword(newPassword)

      if (error) {
        setMessage(error.message)
        setMessageType('error')
      } else {
        setMessage('Password updated successfully! Redirecting to login...')
        setMessageType('success')
        setNewPassword('')
        setConfirmPassword('')
        setTimeout(() => navigate('/login'), 2000)
      }
    } catch (err) {
      setMessage('An unexpected error occurred. Please try again.')
      setMessageType('error')
    } finally {
      setIsLoading(false)
    }
  }

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handlePasswordUpdate()
    }
  }

  // Show loading while checking session
  if (!session || !user) {
    return (
      <div className="ResetPassword-container">
        <div className="ResetPassword-card">
          <Loader />
          <p>Verifying reset link...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="ResetPassword-container">
      <div className="ResetPassword-wrapper">
        {/* Left Section - Image and Subtitle */}
        <div className="ResetPassword-left-section">
          <div className="ResetPassword-image-container">
            <img 
              src={logo} 
              alt="Logo" 
              className="ResetPassword-image"
            />
          </div>
          
          <div className="ResetPassword-subtitle-container">
            <p className="ResetPassword-subtitle">
              {displayedText}
              {showCursor && <span className="ResetPassword-subtitle-cursor"></span>}
            </p>
          </div>
        </div>

        {/* Right Section - Reset Password Form */}
        <div className="ResetPassword-right-section">
          <div className="ResetPassword-card">
            {isLoading && (
              <div className="ResetPassword-loading-overlay">
                <Loader />
              </div>
            )}
            
            <div className="ResetPassword-icon">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
              </svg>
            </div>
            
            <h2 className="ResetPassword-title">Set New Password</h2>
            
            <form className="ResetPassword-form" onSubmit={(e) => { e.preventDefault(); handlePasswordUpdate(); }}>
              <input
                className="ResetPassword-input"
                type="password"
                placeholder="Enter new password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={isLoading}
                required
              />
              
              <input
                className="ResetPassword-input"
                type="password"
                placeholder="Confirm new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={isLoading}
                required
              />
              
              <button
                className="ResetPassword-button"
                type="submit"
                disabled={isLoading || !newPassword || !confirmPassword}
              >
                {isLoading ? <Loader /> : 'Update Password'}
              </button>
            </form>

            {message && (
              <div className={`ResetPassword-message ResetPassword-message--${messageType}`}>
                {message}
              </div>
            )}

            <div className="ResetPassword-info">
              <p className="ResetPassword-info-title">Password requirements:</p>
              <ul className="ResetPassword-requirements">
                <li>At least 8 characters long</li>
                <li>One uppercase letter</li>
                <li>One lowercase letter</li>
                <li>One number</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ResetPassword